import { useApolloClient, useLazyQuery } from '@apollo/client';
import { IonRouterLink, IonText } from '@ionic/react';
import equal from 'deep-equal';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import TertiaryButton from '@components/buttons/TertiaryButton';
import DateStamp from '@components/dates/DateStamp';
import styles from '@components/expert/ExpertDashboardMetricPage.module.css';
import ExpertRoleBoxes from '@components/ExpertRoleBoxes';
import { TimePeriodEnum } from '@components/fields/TimePeriodField';
import ExpertKPIsFilters, { Filters } from '@components/forms/ExpertKPIsFilters';
import HandleQuery from '@components/HandleQuery';
import Nbsp from '@components/Nbsp';
import ExpertTablesPage from '@components/pageTypes/ExpertTablesPage';
import Table, { TableHeader } from '@components/Table';
import Icon from '@components/text/Icon';
import Link from '@components/text/Link';
import PageTitle from '@components/text/PageTitle';
import useSubscribeToJourneys from '@hooks/subscriptions/useSubscribeToJourneys';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import {
  CarePlanStatusEnum,
  CarePlanTurnAroundTimeSortByEnum,
  ClientAccessTypeEnum,
  ExpertGlobalRoleEnum,
  GetExpertCarePlanTurnAroundTimesDocument,
  GetExpertCarePlanTurnAroundTimesQuery,
  GetExpertCarePlanTurnAroundTimesQueryVariables,
  GetExpertOverviewDocument,
  GetExpertOverviewQuery,
  GetExpertOverviewQueryVariables,
  SortDirectionEnum
} from '@typing/Generated';
import { calculateDatesFromTimePeriod } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';
import { minutesToHoursAndMinutes } from '@utils/timeUtils';

const ExpertDashboardCPTurnAroundTimePage = () => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const apolloClient = useApolloClient();

  const [sortBy, setSortBy] = useState<CarePlanTurnAroundTimeSortByEnum>(
    CarePlanTurnAroundTimeSortByEnum.care_plan_turn_around_time
  );
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.descending);

  const [filters, setFilters] = useState<Filters>({
    endDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).endDate,
    expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager ? null : myExpert?.id,
    selfPaid: false,
    startDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).startDate,
    timePeriod: TimePeriodEnum.SEVEN_DAYS
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const [getCarePlans, getCarePlansQuery] = useLazyQuery<
    GetExpertCarePlanTurnAroundTimesQuery,
    GetExpertCarePlanTurnAroundTimesQueryVariables
  >(GetExpertCarePlanTurnAroundTimesDocument);

  const [getExpertOverview, getExpertOverviewQuery] = useLazyQuery<
    GetExpertOverviewQuery,
    GetExpertOverviewQueryVariables
  >(GetExpertOverviewDocument);

  useSubscribeToJourneys(
    () => {
      // This kicks out all of the expertCarePlanTurnAroundTimes queries so we will re-make them. If you're flipping between filters
      // then the queries related to them will be cached, but if a journey is changed then we need to redo those queries.
      apolloClient.cache.evict({ fieldName: 'expertCarePlanTurnAroundTimes', id: 'ROOT_QUERY' });

      getCarePlansQuery.refetch();
    },
    {
      skip: !myExpert
    }
  );

  useEffect(() => {
    if (filters.timePeriod === TimePeriodEnum.CUSTOM && (!filters.startDate || !filters.endDate)) {
      return;
    }
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

    if (myExpert) {
      getExpertOverview({
        variables: {
          endDate: dates.endDate,
          expertId: filters.expertId,
          startDate: dates.startDate
        }
      });
      getCarePlans({
        variables: {
          endDate: dates.endDate,
          expertId: filters.expertId ?? null,
          sortBy,
          sortDirection,
          startDate: dates.startDate
        }
      });
    }
  }, [filters, getCarePlans, getExpertOverview, intl, myExpert, sortBy, sortDirection]);

  const journeys = useNodesFromConnection(getCarePlansQuery.data?.expertCarePlanTurnAroundTimes);

  const statusIconAndLink = (
    <>
      <IonRouterLink
        href="https://www.notion.so/withgrayce/f4a4111e6c4c4b6daee3a3b4b7312e06?v=306b8a3c82d6485ba76e55d1a3d1b29b"
        rel="noopener noreferrer"
        target="_blank"
      >
        <Icon ariaLabelKey="accessibility.ariaLabels.info" className={styles.infoCircle} icon="infoCircle" />
      </IonRouterLink>
    </>
  );

  const tableHeaders: TableHeader<CarePlanTurnAroundTimeSortByEnum>[] = [
    { columnSortBy: CarePlanTurnAroundTimeSortByEnum.client_name },
    { columnSortBy: CarePlanTurnAroundTimeSortByEnum.loved_one_name },
    { columnSortBy: CarePlanTurnAroundTimeSortByEnum.employer_name },
    { columnSortBy: CarePlanTurnAroundTimeSortByEnum.primary_expert },
    { columnSortBy: CarePlanTurnAroundTimeSortByEnum.status, extraInfo: statusIconAndLink },
    { columnSortBy: CarePlanTurnAroundTimeSortByEnum.care_plan_last_updated },
    { columnSortBy: CarePlanTurnAroundTimeSortByEnum.care_plan_turn_around_time }
  ];

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.dashboardCptat.list">
      <PageTitle imageName="peopleCoffeeLight" titleKey={`pages.expert.dashboard.cptat.title`} />
      <div>
        <TertiaryButton
          icon="chevronLeft"
          routerLink={`/expert/dashboard${location.search}`}
          textKey="pages.expert.dashboard.consults.dashboard"
        />
      </div>
      <div className={styles.flex}>
        <div
          className={`${styles.filters} ${filters.timePeriod === TimePeriodEnum.CUSTOM ? styles.biggerFilters : ''}`}
        >
          <ExpertKPIsFilters filters={filters} onChange={handleFilterChange} />
        </div>
        {getExpertOverviewQuery.data && (
          <div className={styles.infoBox}>
            <div className={styles.infoBoxLabel}>
              <FormattedMessage id="pages.expert.dashboard.cptat.average" />
            </div>
            <div className={styles.infoBoxData}>
              {minutesToHoursAndMinutes({
                minutes: getExpertOverviewQuery.data.expertOverview.carePlanTurnAroundTime / 60,
                roundRemainder: true
              })}
            </div>
          </div>
        )}
      </div>

      <HandleQuery query={getCarePlansQuery}>
        {_data => (
          <>
            <div className={styles.tableContainer}>
              <div className={styles.carePlansCount + ' ion-margin-start'}>
                <FormattedMessage
                  id="pages.carePlan.carePlanCount"
                  values={{ count: getCarePlansQuery.data?.expertCarePlanTurnAroundTimes.totalCount }}
                />
              </div>
            </div>
            <Table<CarePlanTurnAroundTimeSortByEnum>
              fetchMore={getCarePlansQuery.fetchMore}
              headerKey="pages.expert.carePlans.tableHeadings"
              headers={tableHeaders}
              pageInfo={getCarePlansQuery.data?.expertCarePlanTurnAroundTimes.pageInfo}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortBy={sortBy}
              sortDirection={sortDirection}
            >
              {journeys.map(journey => (
                <tr key={journey.id}>
                  <td>
                    <div className={styles.bigText}>
                      {journey.owner.lastName}
                      <FormattedMessage id="dictionary.punctuation.comma" />
                      <Nbsp />
                      {journey.owner.firstName}
                    </div>
                  </td>
                  <td>
                    <Link
                      className={styles.bigText}
                      data-test="journey-route"
                      routerLink={journeyRoute({ journeyId: journey.id, linkAfterJourney: '', myExpert })}
                    >
                      {journey.careProfile.name ? (
                        <>{journey.careProfile.name}</>
                      ) : (
                        <FormattedMessage id="dictionary.unknown" />
                      )}
                    </Link>
                  </td>
                  <td>
                    {journey.owner.accessType === ClientAccessTypeEnum.EMPLOYER_SPONSORED ? (
                      journey.owner.employer?.name
                    ) : (
                      <FormattedMessage id="dictionary.selfPaid" />
                    )}
                  </td>
                  <td className={styles.lessPadding}>
                    <ExpertRoleBoxes expertRoles={journey.expertRoles} />
                  </td>
                  <td>
                    <IonText className={journey.carePlanStatus === CarePlanStatusEnum.OVERDUE ? styles.overdue : ''}>
                      {journey.carePlanStatus === CarePlanStatusEnum.OVERDUE && (
                        <Icon ariaLabelKey="accessibility.ariaLabels.alarmClock" icon="alarmClock" />
                      )}
                      <FormattedMessage id={`models.carePlan.status.${journey.carePlanStatus}`} />
                    </IonText>
                  </td>
                  <td>
                    {journey.carePlanUpdatedAt && journey.carePlanPublishedAt && (
                      <DateStamp showWeekday={false} value={journey.carePlanUpdatedAt} />
                    )}
                  </td>
                  <td>
                    {minutesToHoursAndMinutes({
                      minutes: (journey.carePlanTurnAroundTime ?? 0) / 60,
                      roundRemainder: true
                    })}
                  </td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </HandleQuery>
    </ExpertTablesPage>
  );
};

export default ExpertDashboardCPTurnAroundTimePage;
